import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { Alert, FlagIcon, ModalButtonWithForm } from 'components/common';
import { FormGroup, Select } from 'components/common/form';

import type { Course } from 'shared-types';

type FormValues = {
  language: string;
};

type ButtonProps = React.ComponentProps<typeof ModalButtonWithForm>;

interface Props {
  buttonProps?: ButtonProps;
  course: Pick<Course, 'id' | 'language' | 'languageVersions'>;
  selectedLanguage: string | null;
  onSubmitSuccess?: () => Promise<void>;
}

const CourseLanguageSelection = ({
  buttonProps = {},
  course,
  selectedLanguage,
  onSubmitSuccess
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  const handleSubmit = async ({ language }: FormValues) => {
    try {
      if (language !== selectedLanguage) {
        // reset course progress & analytics of the user before switching language version.
        // technically we don't really need to this, but we have decided that
        // this is the preferred approach for now.
        // if we allowed users to freely switch language versions without resetting progress,
        // their open question answers could be in an unexpected language, their video progress
        // might be based on the video lengths of the previous language version,
        // and their H5P/SCORM answers might be based on the files of the previous language version etc.
        const url =
          `/admin/courses/${course.id}/users/${user.id}/analytics` +
          '?deletionReason=change_course_language_version';
        await api.delete(url);
      }

      const response = await api.post(
        `/admin/courses/${course.id}/set-user-course-completion-language`,
        { language }
      );
      if (response.data?.success && onSubmitSuccess) {
        await onSubmitSuccess();
      }
    } catch (err) {
      console.error(err);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const availableLanguages = getCourseLanguageVersionOptions({
    course,
    onlyPublished: true
  });

  const buttonText = selectedLanguage
    ? messages.commonChange
    : messages.commonSelect;

  return Object.keys(availableLanguages).length < 2 ? null : (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonSize="small"
      buttonText={buttonText}
      initialValues={{ language: selectedLanguage }}
      modalActionCategory="default"
      modalActionText={messages.commonConfirm}
      modalTitle={messages.courseLanguageVersion}
      {...buttonProps}
      onSubmit={handleSubmit}
      preventSubmit={true}
      pristineIsValid={false}
    >
      {() => (
        <>
          {selectedLanguage ? (
            <Alert
              style={{ marginBottom: 15 }}
              text={messages.courseLanguageVersionsSelectLanguageWarning}
              type="error-light"
            />
          ) : null}

          <FormGroup label={messages.courseLanguageVersionsSelectLanguageInfo}>
            <Select
              field="language"
              id="language"
              options={Object.entries(availableLanguages).map(
                ([language, data]) => ({
                  label: (
                    <>
                      <FlagIcon
                        lang={language}
                        size="small"
                        style={{ marginRight: 6 }}
                      />
                      {data.capitalizedName}
                    </>
                  ),
                  value: language
                })
              )}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CourseLanguageSelection;
